/**
 * The look of the chimney's option lists — projects, metrics, scores.
 *
 * One place, because the three lists are meant to be *the same* list: ScoreSection says so in as
 * many words ("the list behaves like the metric list beside it"), and a class string copied into
 * three files is a design contract that nothing enforces. Replaces the shared selectors the
 * page's stylesheet used to carry.
 */

/** Scrolling column of options. */
export const optionList = 'flex max-h-80 flex-col gap-0.5 overflow-y-auto';

/**
 * The metric list is the long one — themes, their categories and the points of interest. At its
 * full height it pushed the scores off the bottom of the chimney, which hid the one section a
 * reader needs *after* choosing a metric. Its own, tighter cap keeps both on screen.
 */
export const optionListMetrics = 'max-h-52';

/** One row of a list — a real button, so the whole row is the target. */
export const option =
    'flex min-h-9 w-full items-center gap-2 rounded-md border border-transparent px-2.5 py-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

/** Added to `option` (or `subOption`) for whatever is currently drawn on the map. */
export const optionSelected = 'border-foreground bg-accent font-bold';

/**
 * A single long word — "Energieerzeugung" — has nowhere to wrap and would paint straight over
 * whatever sits to its right. Breaking inside the word is the lesser evil in a narrow column.
 */
export const optionLabel = 'min-w-0 flex-1 text-sm leading-snug [overflow-wrap:anywhere]';

/** Trailing note of a row — a year, a count. Four characters, so it never gives way. */
export const optionMeta = 'shrink-0 text-xs text-muted-foreground';

/**
 * The group a search hit came from. Unlike a year this is a full group name next to a full entry
 * name in a narrow column, so it gives way: it shrinks first and ends in an ellipsis rather than
 * running into the label.
 */
export const optionGroup = 'min-w-0 max-w-[55%] shrink truncate text-xs text-muted-foreground';

/** Marks a row that selects something rather than opening a group. */
export const optionBullet = 'mx-0.5 h-2 w-2 shrink-0 rounded-full border-[1.5px] border-muted-foreground';

/** Die Gruppen einer aufgeklappten Kategorie, eingerückt — eine Stufe flacher als die Einträge einer Gruppe. */
export const categoryList = 'mb-1 ml-3 flex flex-col border-l pl-1.5';

/** The categories of an opened group, indented under it. */
export const subList = 'mb-1.5 ml-6 mt-0.5 flex flex-col gap-px border-l pl-2';

/** One category inside an opened group. */
export const subOption =
    'min-h-9 rounded-md border border-transparent px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

/** Explanatory line under a section — counts, hints, "nothing found". */
export const sectionHint = 'text-sm leading-snug text-muted-foreground';

/** Marks a group row that has an active choice inside it. */
export const optionMarked = 'font-bold';

/** The open/closed arrow at the end of a group row. */
export const optionChevron = 'shrink-0 text-muted-foreground';

/**
 * "Switch everything off." Only rendered when there is something to switch
 * off, so it never sits there greyed out being no use to anyone.
 */
export const clearAll =
    'mb-1.5 flex items-center gap-1 self-start rounded-lg border border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:border-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';
